import { useEffect, useRef } from 'react'
import { Outlet, useNavigate } from 'react-router'
import { useDebounceCallback } from 'usehooks-ts'

import i18n from '@app/i18n'

import { AuthProvider } from '@features/auth'
import { useCartSyncStorage } from '@features/cart'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useModalStore } from '@features/modal'
import ModalRoot from '@features/modal/ui'
import { Overlay } from '@features/overlay'
import { useAddressStore } from '@features/shipping-methods'

import { Footer } from '@widgets/footer'
import { Header } from '@widgets/header'
import { useHeaderStore } from '@widgets/header/model/headerStore'
import MinimalHeader from '@widgets/header/ui/MinimalHeader'
import { ProductGroupMenu } from '@widgets/product-group-menu'

import { client } from '@shared/api/client'
import { DEBOUNCE_CART_INFO } from '@shared/contants/product'
import { PingAuthDocument, PingAuthQueryResult } from '@shared/generated'
import { ContentAreaContainer } from '@shared/ui/content-area-container'
import ScrollToTop from '@shared/ui/scroll-top'

import 'react-loading-skeleton/dist/skeleton.css'
import styles from './index.module.scss'

export default function RootLayout () {
  const { closeModal } = useModalStore()
  const { shopId, zoneId } = useAddressStore()
  const {
    setShop,
    syncCart,
    getCart,
    checkProductsInStock,
    removeZeroAmountItemsInCurrentCart,
    setCurrentCart,
    getCartInfoData,
  } = useCartStore()
  const debouncedCartInfoData = useDebounceCallback(
    getCartInfoData,
    DEBOUNCE_CART_INFO,
  )

  const cart = getCart()
  const navigate = useNavigate()
  const { variant } = useHeaderStore()
  const hasLoadedFavorites = useRef(false)

  useEffect(() => {
    hasLoadedFavorites.current = false
  }, [ shopId ])

  useEffect(() => {
    closeModal('mobileMenu')
    closeModal('cart')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ navigate ])


  // TODO: cart
  useCartSyncStorage()

  useEffect(() => {
    const currentCart = getCart()
    setShop(shopId)

    const handleSync = async () => {
      const data = client.readQuery({
        query: PingAuthDocument,
      }) as PingAuthQueryResult['data']
      const isAuthorized =
        data?.pingAuth && !data?.pingAuth?.requireRegistration

      if (isAuthorized) {
        setCurrentCart({
          items: currentCart?.items || [],
          promocode: currentCart?.promocode,
        })
        await checkProductsInStock()
        await removeZeroAmountItemsInCurrentCart(isAuthorized)
        await syncCart()
      }
    }

    handleSync()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ shopId ])

  useEffect(() => {
    debouncedCartInfoData({
      cartInfo: {
        shop_id: shopId,
        zone_id: zoneId || undefined,
        items: cart?.items.map(({ product_id, amount }) => ({
          product_id,
          amount,
        })),
      },
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ shopId, zoneId, JSON.stringify(cart?.items) ])

  i18n.on('languageChanged', () => {
    client.resetStore()
    debouncedCartInfoData({
      cartInfo: {
        shop_id: shopId,
        zone_id: zoneId || undefined,
        items: cart?.items.map(({ product_id, amount }) => ({
          product_id,
          amount,
        })),
      },
    })
  })

  return (
    <>
      <ScrollToTop />
      <AuthProvider>
        <div className="site">
          <div className="site-top">
            {variant === 'minimal' ? <MinimalHeader /> : <Header />}

            <ContentAreaContainer>
              { variant !== 'minimal' && <ProductGroupMenu className={styles.ProductGroupMenu} /> }
              <Outlet />
            </ContentAreaContainer>
            <ModalRoot />
          </div>
          <div className="site-bottom">
            <Footer />
          </div>
        </div>

        <Overlay />
      </AuthProvider>
    </>
  )
}
